import { useCallback, useEffect, useMemo, useState } from "react";
import { useSwivuBryntumContext } from "./useSwivuBryntumContext";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { KANBAN_FIELD_CONFIG } from "@/modern/constants/kanbanConstants";
import { useSwivuStore } from "./useSwivuStore";
import { KanbanColumnsPersistPayload, KanbanColumnType } from "@/modern/components/ui/kanban";

const UNGROUPED_COLUMN_ID = "swivu-kanban-ungrouped-column";
const NULL_VALUE = null;

const getGroupHeaderTitle = (header: any) => {
  return header?.name ?? header?.tagValue ?? undefined;
};

export const useSwivuKanban = ({ gridRef, storeId, entityType }: { gridRef?: React.RefObject<BryntumGrid>; storeId: string; entityType: string }) => {
  if (!storeId || !entityType) throw new Error("Provide an entity type & entity store id");

  const [isKanbanMode, setIsKanbanMode] = useState(false);
  const [storeUpdateTrigger, setStoreUpdateTrigger] = useState(0);
  const [columns, setColumns] = useState<KanbanColumnType<any>[] | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Record<string, boolean>>({});

  const { project } = useSwivuBryntumContext();
  const { version: storeVersion } = useSwivuStore({ storeId });
  const entityTypeValue = entityType as (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];
  const kanbanFieldConfig = KANBAN_FIELD_CONFIG[entityTypeValue] || [];

  const getActiveConfig = useMemo(
    () => (currentStore: any) => {
      if (!currentStore?.groupers) return null;
      return kanbanFieldConfig.find((config) => currentStore.groupers.some((grouper: any) => grouper.field === config.grouperField));
    },
    [kanbanFieldConfig]
  );

  const store = useMemo(() => {
    if (!project) return null;
    return project.getCrudStore(storeId);
  }, [storeId, project]);

  const sortHeadersByGridOrder = useCallback(
    (items: any[]) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig) return items;
      const orderMap = new Map<string, number>();
      const gridGroupHeadersTitles: string[] = store.groupHeaderRecords.map((groupHeader: any, idx: number) => {

        /* To order by the title of the group headers, we need to get the 'title' but the bryntum group header records don't carry the titles directly we could use .data property to get _groupValue but not using that property is suggested therefore we are relying on the meta property. */
        const title = groupHeader.meta.groupRowFor;
        if (title) {
          orderMap.set(title, idx);
          return title;
        }
      });
      const getOrder = (val: string) => (orderMap.has(val) ? orderMap.get(val)! : gridGroupHeadersTitles.length);
      return [...items].sort((a, b) => getOrder(getGroupHeaderTitle(a)) - getOrder(getGroupHeaderTitle(b)));
    },
    [store, getActiveConfig]
  );

  const allGroupersItems = useMemo(() => {
    if (!isKanbanMode) return [];
    const activeConfig = getActiveConfig(store);
    const grouperItems =
      project?.getCrudStore(activeConfig?.grouperStoreId ?? "")?.query((record: any) => (activeConfig?.grouperStoreFilterFn?.(record) ?? true), true) || [];
    return sortHeadersByGridOrder(grouperItems);
  }, [store, storeUpdateTrigger, kanbanFieldConfig, isKanbanMode, getActiveConfig, project, sortHeadersByGridOrder]);

  const isGroupingApplied = useMemo(() => {
    if (!store?.groupers?.length) return false;
    return store.groupers.some((grouper: any) => kanbanFieldConfig.some((config) => config.grouperField === grouper.field));
  }, [store, storeUpdateTrigger, kanbanFieldConfig]);

  const activeConfig = useMemo(() => getActiveConfig(store), [store, getActiveConfig, isGroupingApplied]);

  const isEmptyValue = useCallback(
    (record: any) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig) return null;
      return !(Array.isArray(record[activeConfig.setterField]) ? record[activeConfig.setterField].length > 0 : record[activeConfig.setterField]);
    },
    [store, getActiveConfig]
  );

  const getUngroupedColumnData = useCallback(() => {
    const activeConfig = getActiveConfig(store);
    if (!store || !activeConfig) return [];
    return store.query((record: any) => !record.isGroupHeader && isEmptyValue(record)) || [];
  }, [store, getActiveConfig, isEmptyValue]);

  const buildColumns = useCallback(() => {
    const activeConfig = getActiveConfig(store);
    if (!store || !activeConfig || !allGroupersItems?.length) return [];

    const setterField = activeConfig.setterField;
    const result: KanbanColumnType<any>[] = allGroupersItems.map((item: any) => {
      const value = item.id ?? NULL_VALUE;
      const title = getGroupHeaderTitle(item);

      const data =
        store.query((record: any) => {
          if (record.isGroupHeader || record.isLinked) return false;
          const recordValue = record[setterField];
          return Array.isArray(recordValue) ? (recordValue ?? []).includes(value) : recordValue === value;
        }) ?? [];

      return { id: `swivu-kanban-column-${value}`, title, value, data };
    });

    if (store.count > 0 && !activeConfig.isNotNullable) {
      result.unshift({
        id: UNGROUPED_COLUMN_ID,
        title: "N/A",
        value: NULL_VALUE,
        data: getUngroupedColumnData() || []
      });
    }

    return result;
  }, [store, allGroupersItems, getActiveConfig, getUngroupedColumnData]);

  const handleOnToggleView = useCallback(
    (viewMode: "kanban" | "grid") => {
      const isKanban = viewMode === "kanban";
      setIsKanbanMode(isKanban);
      const gridInstance = gridRef?.current?.instance;
      if (gridInstance) {
        gridInstance.hidden = isKanban;
        /* There is an issue when reselecting current view from the view menu & state is being applied in the grid again by the useEnableViewTracking hook, that causes the grid to not render properly. I am using this workaround to render the rows properly again after user switches from the kanban view. */
        if (!isKanban) gridInstance.renderContents();
      }
    },
    [gridRef]
  );

  const handleChange = useCallback((newColumns: KanbanColumnType<any>[]) => {
    setColumns(newColumns);
  }, []);

  const handlePersist = useCallback(
    ({ movedItem, targetColumnValue }: KanbanColumnsPersistPayload<any>) => {
      const activeConfig = getActiveConfig(store);
      if (!activeConfig) return;

      if (movedItem) {
        const isArrayField = Array.isArray(movedItem[activeConfig.setterField]);
        if (isArrayField) {
          if (targetColumnValue === NULL_VALUE) {
            movedItem[activeConfig.setterField] = [];
            return;
          }
          const prevValues = movedItem[activeConfig.setterField];
          const valuesSet = new Set([...prevValues, targetColumnValue]);
          movedItem[activeConfig.setterField] = Array.from(valuesSet);
        } else {
          movedItem[activeConfig.setterField] = targetColumnValue;
        }
      }
    },
    [store, getActiveConfig]
  );

  const toggleColumnCollapse = useCallback((columnValue: string) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [columnValue]: !prev[columnValue]
    }));
  }, []);

  const removeFromCurrentColumn = useCallback(
    (itemRecord: any, columnValue: any) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig || itemRecord == null) return;

      const isArrayField = Array.isArray(itemRecord[activeConfig.setterField]);
      if (isArrayField) {
        const prevValues = itemRecord[activeConfig.setterField] ?? [];
        itemRecord[activeConfig.setterField] = prevValues.filter((v: any) => v !== columnValue);
      } else {
        itemRecord[activeConfig.setterField] = NULL_VALUE;
      }
    },
    [store, getActiveConfig]
  );

  const getFilteredHiddenCountByColumnId = useCallback(
    (currentColumns: KanbanColumnType<any>[] | null): Record<string, number> => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig || !currentColumns?.length) return {};

      const setterField = activeConfig.setterField;
      const result: Record<string, number> = {};

      for (const column of currentColumns) {
        const isUngrouped = column.id === UNGROUPED_COLUMN_ID;
        const columnPredicate = isUngrouped
          ? (record: any) => !record.isGroupHeader && isEmptyValue(record)
          : (record: any) => {
              if (record.isGroupHeader || record.isLinked) return false;
              const recordValue = record[setterField];
              const value = column.value;
              return Array.isArray(recordValue) ? (recordValue ?? []).includes(value) : recordValue === value;
            };
        const totalInColumn = (store.query(columnPredicate, true) ?? []).length;
        const visibleInColumn = column.data?.length ?? 0;
        const hidden = totalInColumn - visibleInColumn;
        if (hidden > 0) result[column.id] = hidden;
      }

      return result;
    },
    [store, getActiveConfig, isEmptyValue]
  );

  useEffect(() => {
    if (!store) return;
    const detacher = store.on({
      group: () => setStoreUpdateTrigger((prev) => prev + 1)
    });
    return () => detacher();
  }, [store]);

  useEffect(() => {
    if (!store || !isGroupingApplied) {
      handleOnToggleView("grid");
      return;
    }
    if (!isKanbanMode) {
      setColumns(null);
      return;
    }
    const activeConfig = getActiveConfig(store);
    if (!activeConfig) {
      setColumns(null);
      return;
    }
    setColumns(buildColumns());
  }, [store, isGroupingApplied, storeUpdateTrigger, kanbanFieldConfig, project, isKanbanMode, storeVersion, allGroupersItems, getActiveConfig, buildColumns, handleOnToggleView]);

  return {
    isKanbanMode,
    columns,
    handleOnToggleView,
    isGroupingApplied,
    handleChange,
    handlePersist,
    toggleColumnCollapse,
    collapsedColumns,
    getActiveConfig,
    isEmptyValue,
    removeFromCurrentColumn,
    getFilteredHiddenCountByColumnId,
    activeConfig
  };
};
